import type { BaseAPI } from 'src/portal-bff-api';

import { useMemo } from 'react';

import { CONFIG } from 'src/global-config';
import { Configuration } from 'src/portal-bff-api';
import fetchOverride from 'src/lib/fetch-override';

import { useAuthContext } from 'src/auth/hooks';
import { isValidToken } from 'src/auth/context/amplify/utils';

// ----------------------------------------------------------------------
const useApiConfig = () => {
  const { user } = useAuthContext();

  const accessToken = user?.idToken && isValidToken(user.idToken) ? user.idToken : undefined;

  return useMemo(
    () =>
      new Configuration({
        basePath: CONFIG.serverUrl,
        fetchApi: fetchOverride,
        accessToken,
      }),
    [accessToken]
  );
};

const useWithPreMiddleware = <T extends BaseAPI>(api: T) => {
  const { user } = useAuthContext();

  const accessToken = user?.idToken && isValidToken(user.idToken) ? user.idToken : undefined;

  const authenticatedApi = useMemo(() => {
    if (!accessToken) return api;

    return api.withPreMiddleware(async (context) => {
      const headers = new Headers(context.init.headers);
      headers.set('Authorization', `Bearer ${accessToken}`);
      context.init.headers = headers;
      return context;
    });
  }, [api, accessToken]);

  return {
    api: authenticatedApi,
    isAuthenticated: Boolean(accessToken),
  };
};

// ----------------------------------------------------------------------

export { useApiConfig, useWithPreMiddleware };
